// De meters van het vlak Kwaliteit (W6 ronde 5; research §3.6).
//
// Vier meters, plus de telling van de chatlogs:
//  1. de steekproef — twintig gepubliceerde trends per maand, één toets per
//     trend; de uitslag is de zin van de server ("18 van 20 goed"), ook als er
//     na een herbouw meer beoordeeld is dan de greep telt. De woorden van de
//     toetsen komen van de server (`toetsen`); de lijst per maand kent alleen
//     afgesloten maanden;
//  2. de chat-benchmark — de laatste run van het model van nu, met het verschil
//     met de vorige run onder dezelfde meetlat (één run is nog geen trend), de
//     zin waarop vergeleken is, de vragen waarbij de rechter faalde, en de
//     raming van de modelkosten zónder de rechter (of de reden waarom die er
//     niet is);
//  3. wat nog niet gemeten wordt, elk met zijn reden;
//  4. de funding-extractie — regelingen, wijzigingen per veld, bronpagina's.
//
// Niets hier is een eigen rekensom. Het verschil met de vorige run komt al
// uitgerekend van de server; dit scherm leest alleen het teken om de pijl te
// kiezen. Er is geen drempel vastgesteld voor steekproef of benchmark, dus geen
// verzonnen stoplicht: de stand "leeg" met de reden van de server.

import { useId, useState, type ReactNode } from "react";

import { Card } from "../../components/ui";
import { EmptyReason, LoadError } from "../../components/Empty";
import type { Resource } from "../../lib/useResource";
import type {
  BenchmarkBlok,
  ChatTelling,
  KwaliteitResponse,
  Stand,
  ToetsWoord,
} from "../../contracts";
import { color, radius, sectionLabel, size, weight } from "../../tokens";
import { aantal, Balken, Blok, cijfer, datum, getal, Kpi, noot } from "./BronnenStukken";
import { DuimIcoon, FoutIcoon, KlokIcoon, KruisIcoon, VinkIcoon } from "./Iconen";
import { maandNaam, usd } from "./KostenStukken";
import { SteekproefBeoordelen } from "./KwaliteitSteekproef";
import { Stoplicht } from "./Stoplicht";
import { knopLicht } from "./stijl";
import { BENCHMARK_MAAT, DUIM_LABEL, FOUT_LABEL, type BenchmarkMaat } from "./woorden";

type Steekproef = KwaliteitResponse["steekproef"];
type Funding = KwaliteitResponse["funding"];

/** Twee blokken naast elkaar op een breed scherm, onder elkaar op een smal. */
const naastElkaar = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
  gap: 22,
  alignItems: "start",
} as const;

const groteRegel = {
  fontSize: 22,
  fontWeight: weight.bold,
  color: color.ink,
  letterSpacing: "-0.01em",
  lineHeight: 1.25,
} as const;

const telGetal = {
  fontSize: 20,
  fontWeight: weight.bold,
  color: color.ink,
  lineHeight: 1.1,
  margin: 0,
  ...cijfer,
} as const;

const kaal = {
  listStyle: "none",
  padding: 0,
  margin: 0,
  display: "flex",
  flexDirection: "column",
  gap: 8,
} as const;

const subkop = { ...sectionLabel, marginBottom: 10 } as const;

/** Alleen voor een schermlezer: de richting van een verschil in woorden. */
const voorlezen = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

/** "3,45" — een gemiddelde zoals de server het stuurde. */
function decimaal(n: number): string {
  return n.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function Meters({ bron }: { bron: Resource<KwaliteitResponse> }) {
  const [beoordelen, setBeoordelen] = useState(false);
  const lijstId = useId();

  // Zonder eerdere meters is er niets te tonen dan de reden. Mislukt een
  // herlaadronde (na een oordeel, of terug van de chatlogs) terwijl er al
  // meters staan, dan blijven die staan — mét de beoordeellijst, anders
  // verdwijnt het werk van de beheerder — en staat de fout erboven.
  if (bron.error && !bron.data) {
    return (
      <Card style={{ padding: 18 }}>
        <LoadError reason={bron.error} onRetry={bron.reload} />
      </Card>
    );
  }
  if (!bron.data) {
    return (
      <Card style={{ padding: 18 }}>
        <EmptyReason reason="de meters worden opgehaald" />
      </Card>
    );
  }

  const k = bron.data;
  // Wat er staat is van de vorige keer ophalen: licht gedimd, zoals de andere
  // tabellen (terug van de chatlogs, of na een oordeel). De beoordeellijst
  // dimt zelf, dus die valt hier buiten.
  const oud = { opacity: bron.stale ? 0.6 : 1, transition: "opacity 0.15s" } as const;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
      {bron.error && <MetersNietVerversd reden={bron.error} onOpnieuw={bron.reload} />}
      <div data-meters-blok style={{ ...naastElkaar, ...oud }}>
        <SteekproefMeter
          steekproef={k.steekproef}
          toetsen={k.toetsen}
          open={beoordelen}
          lijstId={lijstId}
          onKnop={() => setBeoordelen((o) => !o)}
        />
        <BenchmarkMeter benchmark={k.benchmark} />
      </div>
      {/* Na een oordeel haalt de lijst zichzelf én deze meters opnieuw op:
          de uitslag is die van de server, nooit een eigen telling. */}
      {beoordelen && <SteekproefBeoordelen id={lijstId} onGewijzigd={bron.reload} />}
      <div data-meters-blok style={{ ...naastElkaar, ...oud }}>
        <NogNietGemeten lijst={k.nogNietGemeten} />
        <ChatMeter chat={k.chat} />
      </div>
      <div data-meters-blok style={oud}>
        <FundingMeter funding={k.funding} />
      </div>
      <p style={{ ...noot, marginTop: 0, ...oud }}>
        {`Gemeten op ${datum(k.gemetenOp) ?? "een onbekend moment"}.`}
      </p>
    </div>
  );
}

/** Een herlaadronde die mislukte terwijl er al meters staan: de reden, en dat
 *  wat eronder staat van de vorige keer is. Nooit het hele vlak weg. */
function MetersNietVerversd({ reden, onOpnieuw }: { reden: string; onOpnieuw: () => void }) {
  return (
    <div
      role="alert"
      data-meters-fout
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "6px 12px",
        padding: "10px 14px",
        border: `1px solid ${color.line}`,
        borderRadius: radius.block,
        background: color.lineFaint,
        fontSize: size.meta,
        color: color.body,
        lineHeight: 1.5,
      }}
    >
      <span style={{ color: color.accentRed, display: "inline-flex" }}>
        <FoutIcoon />
      </span>
      <span style={{ flex: "1 1 260px" }}>
        {"De meters zijn niet ververst; wat hieronder staat, is van de vorige keer. "}
        <span style={{ color: color.soft }}>{reden}</span>
      </span>
      <button type="button" onClick={onOpnieuw} style={knopLicht}>
        Opnieuw proberen
      </button>
    </div>
  );
}

// ── 1. De steekproef ─────────────────────────────────────────────────────────

function SteekproefMeter({
  steekproef: s,
  toetsen,
  open,
  lijstId,
  onKnop,
}: {
  steekproef: Steekproef;
  /** De drie toetsen met het woord van de server — de enige woordenlijst. */
  toetsen: ToetsWoord[];
  open: boolean;
  lijstId: string;
  onKnop: () => void;
}) {
  const u = s.uitslag;
  return (
    <Blok
      titel={`Steekproef · ${s.maandNaam}`}
      rechts={
        <button
          type="button"
          aria-expanded={open}
          aria-controls={open ? lijstId : undefined}
          onClick={onKnop}
          style={{ ...knopLicht, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          {open ? <KruisIcoon /> : <VinkIcoon />} {open ? "Lijst sluiten" : "Beoordelen"}
        </button>
      }
    >
      <Card style={{ padding: "18px 20px" }}>
        <div data-steekproef>
          <div style={groteRegel}>{u.tekst}</div>
          <dl style={{ display: "flex", flexWrap: "wrap", gap: 26, margin: "14px 0 0" }}>
            {toetsen.map((t) => (
              <div key={t.sleutel} data-toets-telling={t.sleutel}>
                <dt style={{ ...sectionLabel, marginBottom: 3 }}>{t.label}</dt>
                <dd style={telGetal}>{getal(u[t.sleutel])}</dd>
              </div>
            ))}
          </dl>
          <StandRegel stand={u.stand} reden={u.reden} />
          <Historie historie={s.historie} />
        </div>
      </Card>
    </Blok>
  );
}

/**
 * Het stoplicht met de reden van de server ernaast. Zonder drempel is de stand
 * "leeg" en zegt de reden waarom; die zin staat dan ook in het stoplicht zelf,
 * anders zou het "niet gemeten" zeggen — en de steekproef is wél gemeten.
 */
function StandRegel({ stand, reden }: { stand: Stand; reden: string | null }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginTop: 14,
        fontSize: size.meta,
        color: color.body,
        lineHeight: 1.5,
      }}
    >
      <Stoplicht stand={stand} reden={reden} />
      {reden && <span>{reden}</span>}
    </div>
  );
}

/** De uitslag per AFGESLOTEN maand — de lopende maand is de uitslag hierboven.
 *  De server stuurt de nieuwste maand eerst; dit scherm sorteert niet opnieuw en
 *  toont per maand zijn zin. */
function Historie({ historie }: { historie: Steekproef["historie"] }) {
  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${color.lineFaint}` }}>
      <div style={{ ...sectionLabel, marginBottom: 8 }}>Eerdere maanden</div>
      {historie.length === 0 ? (
        <EmptyReason reason="nog geen afgesloten maand — de lopende maand staat hierboven" />
      ) : (
        <ul aria-label="Steekproef per maand" style={{ ...kaal, gap: 6 }}>
          {historie.map((h) => (
            <li
              key={h.maand}
              data-maand={h.maand}
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "2px 10px",
                fontSize: size.meta,
                color: color.body,
              }}
            >
              <Stoplicht stand={h.stand} reden={h.reden} />
              <span style={{ fontWeight: weight.medium, color: color.ink, minWidth: 110 }}>
                {h.maandNaam}
              </span>
              <span>{h.tekst}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── 2. De chat-benchmark ─────────────────────────────────────────────────────

function BenchmarkMeter({ benchmark: b }: { benchmark: BenchmarkBlok }) {
  const l = b.laatste;
  const v = b.verschil;
  const vorige = b.vorige ? [b.vorige.variant, datum(b.vorige.datum)].filter(Boolean).join(", ") : "";
  const wat = l ? [l.variant, l.beschrijving].filter(Boolean).join(" · ") : "";
  // `datum` kan null zijn (geen startmoment en geen datum in de runnaam): dan
  // niets. Een onleesbare datum staat er kaal, zoals de server hem stuurde.
  const wanneer = l?.datum ? (datum(l.datum) ?? l.datum) : null;

  return (
    <Blok titel="Chat-benchmark">
      <Card style={{ padding: "18px 20px" }}>
        <div data-benchmark>
          {l && (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 10 }}>
                <span style={groteRegel}>
                  {`${getal(l.groen)} van ${aantal(l.vragen, "vraag", "vragen")} groen`}
                </span>
                {v && <Verschil n={v.groen} decimalen={0} />}
              </div>
              <p style={{ fontSize: size.meta, color: color.body, marginTop: 6, lineHeight: 1.5 }}>
                {wat || <EmptyReason reason="de run draagt geen naam of beschrijving" />}
              </p>
              <p style={{ fontSize: 12, color: color.soft, marginTop: 2 }}>
                {l.model ?? <EmptyReason reason="model niet vastgelegd" />}
                {/* Zonder datum van de server staat er geen datum: nooit een verzonnen dag. */}
                {wanneer ? ` · ${wanneer}` : ""}
                {` · ${getal(l.beoordeeld)} van ${aantal(
                  l.vragen,
                  "vraag",
                  "vragen",
                )} beoordeeld door de rechter`}
              </p>
              {l.rechterFaalde > 0 && (
                <p
                  data-rechter-faalde
                  style={{
                    fontSize: size.meta,
                    color: color.body,
                    marginTop: 6,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <FoutIcoon />{" "}
                  {`${aantal(l.rechterFaalde, "vraag", "vragen")} zonder oordeel: de rechter faalde`}
                </p>
              )}

              <dl
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 12,
                  margin: "14px 0 0",
                }}
              >
                {(Object.keys(BENCHMARK_MAAT) as BenchmarkMaat[]).map((m) => {
                  const n = l.gemiddelden[m];
                  return (
                    <div key={m} data-maat={m}>
                      <dt style={{ ...sectionLabel, marginBottom: 3 }}>{BENCHMARK_MAAT[m]}</dt>
                      <dd style={{ margin: 0 }}>
                        {n !== null ? (
                          <span style={telGetal}>{decimaal(n)}</span>
                        ) : (
                          <EmptyReason reason="niet beoordeeld" />
                        )}
                        {v && (
                          <div style={{ marginTop: 3 }}>
                            <Verschil n={v[m]} decimalen={2} />
                          </div>
                        )}
                      </dd>
                    </div>
                  );
                })}
              </dl>
              {/* Waarop vergeleken is (of waarom niet): de zin van de server,
                  direct onder de getallen waar hij over gaat. */}
              {b.verschilUitleg && (
                <p data-verschil-uitleg style={noot}>
                  {b.verschilUitleg}
                </p>
              )}
              {vorige && (
                <p style={{ ...noot, marginTop: b.verschilUitleg ? 4 : noot.marginTop }}>
                  {`Pijlen: verschil met de vorige run van hetzelfde model (${vorige}).`}
                </p>
              )}

              {/* Het waarschuwingsteken alleen als er echt een check faalde:
                  bij nul is er niets om voor te waarschuwen. */}
              <p
                data-checks
                style={{
                  fontSize: size.meta,
                  color: color.body,
                  marginTop: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {l.checksGevallen > 0 && <FoutIcoon />}
                {`${aantal(l.checksGevallen, "vraag", "vragen")} met een code-check die faalde`}
              </p>
              {/* Het bedrag is een raming van de server: de optelsom per vraag,
                  zónder de rechter. Dat voorbehoud staat erbij; zonder bedrag
                  staat zijn reden er, nooit een eigen schatting. */}
              <p style={{ fontSize: size.meta, color: color.body, marginTop: 4 }}>
                {"Raming modelkosten, zonder de rechter: "}
                {l.kostenUsd !== null ? (
                  <strong style={{ fontWeight: weight.medium, color: color.ink }}>
                    {usd(l.kostenUsd)}
                  </strong>
                ) : (
                  <EmptyReason reason={l.kostenReden ?? "de run legde geen bedrag vast"} />
                )}
              </p>
              {/* Geen noot over een onafgemaakte run: de server stuurt die nooit
                  als laatste of vorige (backend-review ronde 5). */}
              {l.regelsOnleesbaar > 0 && (
                <p data-regels-onleesbaar style={{ ...noot, marginTop: 4 }}>
                  {`${aantal(l.regelsOnleesbaar, "regel", "regels")} in het verslag van deze run ${
                    l.regelsOnleesbaar === 1 ? "was" : "waren"
                  } niet te lezen.`}
                </p>
              )}
            </>
          )}

          {/* Zonder vorige run (of zonder run) zegt de server waarom; die zin
              staat hier letterlijk, met een klokje: het komt nog. */}
          {b.reden ? (
            <p
              role="note"
              data-benchmark-reden
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                marginTop: l ? 12 : 0,
                fontSize: size.meta,
                color: color.accentAmber,
                lineHeight: 1.5,
              }}
            >
              <span style={{ marginTop: 2 }}>
                <KlokIcoon />
              </span>
              <span>{b.reden}</span>
            </p>
          ) : (
            !l && <EmptyReason reason="de server stuurde geen run en geen reden" />
          )}
          {/* Zonder run zijn er geen getallen om onder te staan; stuurt de server
              tóch een uitleg mee, dan valt die niet stil weg. */}
          {!l && b.verschilUitleg && (
            <p data-verschil-uitleg style={noot}>
              {b.verschilUitleg}
            </p>
          )}
          <p style={noot}>{b.groenEisen}</p>
          <p style={{ ...noot, marginTop: 4 }}>{b.opnieuw}</p>
        </div>
      </Card>
    </Blok>
  );
}

/**
 * Het verschil met de vorige run, zoals de server het uitrekende: met teken en
 * pijl. Het teken lezen is geen rekensom — het getal zelf gaat ongewijzigd
 * door. Bewust zonder groen of rood: zonder vastgestelde drempel is +0,12 geen
 * oordeel, alleen een richting.
 */
function Verschil({ n, decimalen }: { n: number | null; decimalen: number }) {
  if (n === null) return <EmptyReason reason="niet te vergelijken" />;
  const kaalGetal = n.toLocaleString("nl-NL", {
    minimumFractionDigits: decimalen,
    maximumFractionDigits: decimalen,
  });
  const tekst = n > 0 ? `+${kaalGetal}` : kaalGetal;
  const richting =
    n > 0 ? "hoger dan de vorige run" : n < 0 ? "lager dan de vorige run" : "gelijk aan de vorige run";
  return (
    <span
      data-verschil
      title={richting}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontSize: 12,
        color: color.soft,
        ...cijfer,
      }}
    >
      <span aria-hidden>{n > 0 ? "↑" : n < 0 ? "↓" : "→"}</span>
      <span>{tekst}</span>
      <span style={voorlezen}>{richting}</span>
    </span>
  );
}

// ── 3. Nog niet gemeten ──────────────────────────────────────────────────────

function NogNietGemeten({ lijst }: { lijst: KwaliteitResponse["nogNietGemeten"] }) {
  return (
    <Blok titel="Nog niet gemeten">
      <Card style={{ padding: "14px 18px" }}>
        {lijst.length === 0 ? (
          <EmptyReason reason="de server meldt geen meter die nog ontbreekt" />
        ) : (
          <ul style={{ ...kaal, gap: 12 }}>
            {lijst.map((m) => (
              <li
                key={m.sleutel}
                data-nog-niet
                style={{ display: "flex", gap: 10, alignItems: "flex-start" }}
              >
                <span style={{ color: color.faint, marginTop: 2 }}>
                  <KlokIcoon />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: size.meta, fontWeight: weight.medium, color: color.body }}>
                    {m.wat}
                  </div>
                  <div style={{ fontSize: 12, color: color.faint, lineHeight: 1.5, marginTop: 2 }}>
                    {m.reden}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Blok>
  );
}

// ── De telling van de chatlogs ───────────────────────────────────────────────

function ChatMeter({ chat: c }: { chat: ChatTelling }) {
  const rijen: { sleutel: string; label: string; n: number; icoon?: ReactNode }[] = [
    { sleutel: "totaal", label: "bewaard", n: c.totaal },
    { sleutel: "dezeMaand", label: "deze maand", n: c.dezeMaand },
    { sleutel: "duimOp", label: DUIM_LABEL.op, n: c.duimOp, icoon: <DuimIcoon /> },
    { sleutel: "duimNeer", label: DUIM_LABEL.neer, n: c.duimNeer, icoon: <DuimIcoon omlaag /> },
    { sleutel: "metFout", label: FOUT_LABEL.filter, n: c.metFout, icoon: <FoutIcoon /> },
  ];
  return (
    <Blok titel="Chatgesprekken">
      <Card style={{ padding: "14px 18px" }}>
        <dl
          data-chat
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(104px, 1fr))",
            gap: 14,
            margin: 0,
          }}
        >
          {rijen.map((r) => (
            <div key={r.sleutel} data-chat-telling={r.sleutel}>
              <dt
                style={{ ...sectionLabel, marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}
              >
                {r.icoon}
                {r.label}
              </dt>
              <dd style={telGetal}>{getal(r.n)}</dd>
            </div>
          ))}
        </dl>
        <p style={noot}>{c.bewaartermijn}</p>
        <p style={{ ...noot, marginTop: 4 }}>De gesprekken zelf staan onder het tabblad Chatlogs.</p>
      </Card>
    </Blok>
  );
}

// ── 4. De funding-extractie ──────────────────────────────────────────────────

function FundingMeter({ funding: f }: { funding: Funding }) {
  // Alleen om de balken te TEKENEN (zie BronnenStukken): het label is de telling
  // van de server, nooit een aandeel dat hij niet stuurde.
  const wijzigSchaal = Math.max(0, ...f.wijzigingenPerVeld.map((w) => w.aantal));
  const bronSchaal = Math.max(0, ...f.bronpaginas.map((b) => b.aantal));

  return (
    <Blok titel="Funding-extractie">
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div
          data-funding
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12,
          }}
        >
          <Kpi label="Regelingen" waarde={getal(f.regelingen)} />
          {/* Reviewronde W1: open/gesloten zijn BEREKEND op vandaag, "venster
              verlopen" telt de achterlopende OPGESLAGEN status. Zonder deze
              regels lijkt dezelfde regeling dubbel geteld. */}
          <Kpi label="Open" waarde={getal(f.open)} onder="berekend op vandaag" />
          <Kpi label="Gesloten" waarde={getal(f.gesloten)} onder="berekend op vandaag" />
          <Kpi
            label="Venster verlopen"
            waarde={getal(f.vensterVerlopen)}
            onder="opgeslagen status loopt achter op het venster; al meegeteld bij gesloten"
          />
          <Kpi label="Nieuw deze maand" waarde={getal(f.nieuwDezeMaand)} />
          <Kpi
            label="Laatst gecontroleerd"
            waarde={datum(f.laatstGecontroleerd)}
            reden="nog nooit gecontroleerd"
          />
        </div>

        <div style={naastElkaar}>
          <Card style={{ padding: "14px 18px" }}>
            <h3 style={subkop}>Wijzigingen per veld</h3>
            {f.wijzigingenPerVeld.length > 0 ? (
              <Balken
                schaal={wijzigSchaal}
                rijen={f.wijzigingenPerVeld.map((w) => {
                  const wanneer = datum(w.laatst);
                  return {
                    sleutel: w.veld,
                    label: w.label,
                    n: w.aantal,
                    title: wanneer ? `laatst gewijzigd op ${wanneer}` : undefined,
                  };
                })}
              />
            ) : (
              <EmptyReason reason={f.wijzigingenReden ?? "de server meldt geen wijzigingen"} />
            )}
            {f.wijzigingenPerVeld.length > 0 && f.wijzigingenReden && (
              <p style={noot}>{f.wijzigingenReden}</p>
            )}
          </Card>
          <Card style={{ padding: "14px 18px" }}>
            <h3 style={subkop}>Bronpagina&apos;s</h3>
            {f.bronpaginas.length > 0 ? (
              <Balken
                schaal={bronSchaal}
                rijen={f.bronpaginas.map((b) => ({ sleutel: b.resultaat, label: b.label, n: b.aantal }))}
              />
            ) : (
              <EmptyReason reason="de server meldt nog geen gecontroleerde bronpagina's" />
            )}
          </Card>
        </div>

        <p style={{ ...noot, marginTop: 0 }} data-nieuw-per-maand>
          {"Nieuwe regelingen per maand: "}
          {f.nieuwPerMaand.length > 0 ? (
            f.nieuwPerMaand.map((r) => `${maandNaam(r.maand)} ${getal(r.nieuw)}`).join(" · ")
          ) : (
            <EmptyReason reason="nog geen maand geteld" />
          )}
        </p>
      </div>
    </Blok>
  );
}
